/**
 * Offline read cache for operator data (manifest, schedule, calendar, crew…).
 *
 * Every successful GET on a cacheable endpoint is silently stored in IndexedDB.
 * When the device is offline (network-level failure, no HTTP response) the same
 * request is answered from the cache instead of failing, and a
 * `bf-offline-data` event fires so the UI can show the "offline" banner.
 *
 * The same DB also holds the offline ACTION QUEUE (see offlineQueue.js).
 */

const DB_NAME = 'bf-offline';
const DB_VERSION = 2;
export const CACHE_STORE = 'apiCache';
export const QUEUE_STORE = 'actionQueue';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // prune entries older than 7 days

// Operator endpoints worth serving offline (GET only)
const CACHEABLE = [
  /\/organizations\/[^/]+\/bookings/,
  /\/organizations\/[^/]+\/calendar/,
  /\/organizations\/[^/]+\/services/,
  /\/organizations\/[^/]+\/assets/,
  /\/organizations\/[^/]+\/team-members/,
  /\/organizations\/[^/]+\/roles/,
  /\/organizations\/[^/]+\/customers/,
  /\/organizations\/[^/]+\/settings\/waivers\/search/,
  /\/organizations\/[^/]+\/settings\/standalone-waivers/,
  /\/schedule\/daily/,
  /\/auth\/organizations\/me/,
];

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') return reject(new Error('no indexedDB'));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(CACHE_STORE)) db.createObjectStore(CACHE_STORE, { keyPath: 'key' });
      if (!db.objectStoreNames.contains(QUEUE_STORE)) db.createObjectStore(QUEUE_STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

export async function idbPut(store, entry) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).put(entry);
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbGet(store, key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(store, 'readonly').objectStore(store).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function idbAll(store) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(store, 'readonly').objectStore(store).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

export async function idbDelete(store, key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).delete(key);
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

async function pruneOld() {
  try {
    const db = await openDb();
    const cutoff = Date.now() - MAX_AGE_MS;
    const tx = db.transaction(CACHE_STORE, 'readwrite');
    const store = tx.objectStore(CACHE_STORE);
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      if ((cursor.value?.ts || 0) < cutoff) cursor.delete();
      cursor.continue();
    };
  } catch {
    /* cache is best-effort */
  }
}

function isCacheableGet(config) {
  if (!config || (config.method || 'get').toLowerCase() !== 'get') return false;
  const url = config.url || '';
  return CACHEABLE.some((re) => re.test(url));
}

function cacheKey(config) {
  const orgId = localStorage.getItem('organizationId') || 'noorg';
  const params = config.params ? JSON.stringify(config.params) : '';
  return `${orgId}::${config.url}::${params}`;
}

/** True when the failure is network-level (offline/timeout), not an HTTP error. */
export function isNetworkFailure(error) {
  return !error.response && (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || error.message === 'Network Error' || !navigator.onLine);
}

export function attachOfflineCache(axiosInstance) {
  axiosInstance.interceptors.response.use(
    (response) => {
      if (isCacheableGet(response.config)) {
        idbPut(CACHE_STORE, { key: cacheKey(response.config), ts: Date.now(), data: response.data }).catch(() => {});
      }
      return response;
    },
    async (error) => {
      if (isCacheableGet(error.config) && isNetworkFailure(error)) {
        try {
          const hit = await idbGet(CACHE_STORE, cacheKey(error.config));
          if (hit) {
            window.dispatchEvent(new CustomEvent('bf-offline-data', { detail: { cachedAt: hit.ts } }));
            return {
              data: hit.data,
              status: 200,
              statusText: 'OK (offline cache)',
              headers: {},
              config: error.config,
              fromOfflineCache: true,
              cachedAt: hit.ts,
            };
          }
        } catch {
          /* fall through to the original error */
        }
      }
      return Promise.reject(error);
    }
  );
  pruneOld();
}
